/**
 * In-memory holder for the SSO JWT.
 *
 * The embedded API accepts either the `np-sending` session cookie or the SSO
 * token as a Bearer header. We prefer the header because a cookie cannot be
 * used when an FI embeds this app in an iframe on their own domain: the browser
 * treats every request from that frame as cross-site, so it refuses to store or
 * send a SameSite=Strict cookie, and Safari blocks third-party cookies outright
 * no matter what SameSite says.
 *
 * Relaxing the cookie to SameSite=None would fix the frame but hand every site
 * on the internet an authenticated copy of this app to embed, since nothing in
 * our serving path sets frame-ancestors. A Bearer token has no such problem: it
 * is never attached automatically, so a hostile frame gets nothing.
 *
 * Deliberately a module variable and NOT localStorage or sessionStorage. Web
 * storage is readable by any script on the origin and survives the tab, which
 * is exactly what we do not want for a bearer credential. Losing it on reload
 * is intended — the parent frame reloads us with a fresh `token` query param,
 * and outside a frame the session cookie still covers the reload.
 */

let sessionToken = null;

/** Stores the SSO token for subsequent API calls. */
export function setSessionToken(token) {
  sessionToken = token || null;
}

/** Returns the SSO token, or null when we are running on the cookie alone. */
export function getSessionToken() {
  return sessionToken;
}

/** Drops the token. Called on sign-out. */
export function clearSessionToken() {
  sessionToken = null;
}
